import { type FormEvent, type ReactNode, useEffect, useMemo, useState } from 'react';
import { Link, Navigate, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { ArrowLeft, Check, CircleAlert, Clock3, Eye, EyeOff, Info, KeyRound, Link2Off, Lock, ShieldCheck } from 'lucide-react';
import { isApiError } from '@missions/api-client';
import { Button } from '@missions/ui';
import { api } from '../../lib/client';
import { useApi } from '../../lib/hooks';
import { setPageTitle } from '../../lib/seo';
import { useSession, useT } from '../../lib/session';
import { Radar } from '../../components/atoms';
import '../../styles/auth.css';

/* ===========================================================================
   Tenant-branded auth screens (mockups/auth.html): split layout with a
   branded showcase on the right, slim header on phones.
   ========================================================================= */

function AuthLayout({ children, top }: { children: ReactNode; top?: ReactNode }) {
  const s = useSession();
  const t = useT();
  const mission = t.tMinusDates;
  const logo = s.tenant.logoUrl;
  return (
    <div className="pg-auth">
      <div className="auth">
        <main className="pane">
          <div className="pane-top">
            <Link to="/" className="brand-lockup" aria-label={`${s.tenant.appTitle} home`}>
              {logo ? (
                <>
                  <img src={logo} alt="" />
                  <span className="nm">{s.tenant.appTitle}</span>
                </>
              ) : (
                <>
                  <img className="brand-light" src="/brand/missions-logo.png" alt="Missions" />
                  <img className="brand-dark" src="/brand/missions-logo-dark.png" alt="Missions" />
                </>
              )}
            </Link>
            <span className="muted">{top}</span>
          </div>
          <div className="pane-main">
            <div className="stack">
              <section className="screen on">{children}</section>
            </div>
          </div>
          <footer className="pane-foot">
            {s.tenant.showPoweredBy ? (
              <a className="pw pwr-dnw" href="https://devninjaweb.com" target="_blank" rel="noopener">Powered by <img src="/brand/dnw-logo.png" alt="DevNinjaWeb" /></a>
            ) : (
              <span />
            )}
            <span><a href="https://devninjaweb.com/privacy" target="_blank" rel="noopener">Privacy</a> · <a href="https://devninjaweb.com/terms" target="_blank" rel="noopener">Terms</a></span>
          </footer>
        </main>
        <aside className="show" aria-hidden="true">
          <div className="show-top"><span>{s.tenant.appTitle}</span><span className="pill">{s.tenant.tagline ?? (mission ? 'Every mission, accomplished.' : 'Plan, assign & track work')}</span></div>
          <div className="stage">
            <div className="fc noti"><div className="av2">AK</div><div><p><b>Aarav</b> mentioned you on <b>Homepage hero copy</b></p><small>2 min ago</small></div></div>
            <div className="fc task">
              <div className="proj"><i /><span>Website Redesign</span></div>
              <h4>Finalize homepage hero copy</h4>
              <div className="meta">
                {mission && <span className="ms-code" style={{ color: 'var(--text-3)' }}>ACM-142</span>}
                <span className="tag st">In Progress</span>
                {mission ? <span className="ms-t today">T-0 Today</span> : <span className="tag due">Due today</span>}
                <span className="tag pri">High</span>
              </div>
              <div className="foot">
                <div className="avs"><span style={{ background: '#db2777' }}>PS</span><span style={{ background: '#7c3aed' }}>AK</span><span style={{ background: '#059669' }}>RJ</span></div>
                <div className="subt"><span>2/3 {t.subItems}</span><span className="bar"><i /></span></div>
              </div>
            </div>
            <div className="fc due">
              <div className="ic"><Clock3 /></div>
              <div><b>3 {t.items}</b>{mission ? <small style={{ fontFamily: 'var(--font-mono)' }}>T-0 · launch today</small> : <small>due today</small>}</div>
            </div>
            {mission && (
              <div className="fc obj"><span className="ok"><Check /></span><div><span className="ms-code">Mission accomplished</span><b>Client sign-off received</b></div></div>
            )}
            {mission && <div className="show-radar"><Radar size={220} /></div>}
            <div className="fc team"><div className="avs"><span style={{ background: '#0ea5e9' }}>SM</span><span style={{ background: '#f59e0b' }}>NK</span><span style={{ background: '#db2777' }}>PS</span><span style={{ background: '#334155' }}>+9</span></div><small>12 teammates online</small></div>
          </div>
          <div className="quote">
            <p>Know who’s doing what, and by when, without chasing anyone on WhatsApp.</p>
            <div className="who"><span>DN</span>{s.tenant.appTitle} team</div>
          </div>
        </aside>
      </div>
    </div>
  );
}

function PwInput({ id, value, onChange, auto, placeholder, describedBy }: { id: string; value: string; onChange: (v: string) => void; auto: string; placeholder: string; describedBy?: string }) {
  const [show, setShow] = useState(false);
  return (
    <div className="pw-wrap">
      <input className="ui-input" id={id} type={show ? 'text' : 'password'} autoComplete={auto} placeholder={placeholder} value={value} onChange={(e) => onChange(e.target.value)} aria-describedby={describedBy} />
      <button type="button" className="pw-toggle" aria-label={show ? 'Hide password' : 'Show password'} data-tooltip={show ? 'Hide password' : 'Show password'} onClick={() => setShow((x) => !x)}>
        {show ? <EyeOff size={18} /> : <Eye size={18} />}
      </button>
    </div>
  );
}

const RULES: [string, (p: string) => boolean][] = [
  ['At least 10 characters', (p) => p.length >= 10],
  ['Upper & lowercase', (p) => /[a-z]/.test(p) && /[A-Z]/.test(p)],
  ['At least one number', (p) => /\d/.test(p)],
  ['One symbol (!@#…)', (p) => /[^A-Za-z0-9]/.test(p)],
];
export const pwScore = (p: string) => RULES.filter(([, f]) => f(p)).length;

export function PasswordRules({ pw, id }: { pw: string; id: string }) {
  const score = pwScore(pw);
  const label = !pw ? '' : ['Too weak', 'Weak', 'Fair', 'Good', 'Strong'][score];
  return (
    <>
      <div className="meter" aria-hidden="true">
        {[0, 1, 2, 3].map((i) => (
          <i key={i} style={pw && i < Math.max(1, score) ? { background: ['var(--danger)', '#f2751a', '#e59400', 'var(--success)'][Math.max(0, score - 1)] } : undefined} />
        ))}
      </div>
      <div className="meter-label" aria-live="polite">{label}</div>
      <ul className="rules" id={id}>
        {RULES.map(([l, f]) => (
          <li key={l} className={f(pw) ? 'ok' : ''}>
            <span className="tick"><Check size={10} strokeWidth={4} /></span>
            {l}
          </li>
        ))}
      </ul>
    </>
  );
}

/* ---------------- Sign in ---------------- */
export function SignIn() {
  const s = useSession();
  const t = useT();
  const nav = useNavigate();
  const [sp] = useSearchParams();
  const [email, setEmail] = useState('');
  const [pw, setPw] = useState('');
  const [remember, setRemember] = useState(true);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<{ left?: number } | null>(null);
  const [lockedUntil, setLockedUntil] = useState<number | null>(null);
  const [now, setNow] = useState(Date.now());
  const expired = sp.get('expired') === '1';
  useEffect(() => setPageTitle('Sign in'), []);
  useEffect(() => {
    if (!lockedUntil) return;
    const i = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(i);
  }, [lockedUntil]);
  if (s.status === 'signed-in') return <Navigate to={sp.get('next') ?? '/'} replace />;

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!email || !pw) {
      setErr({});
      return;
    }
    setBusy(true);
    setErr(null);
    try {
      await s.signIn(email.trim(), pw, remember);
      nav(sp.get('next') ?? '/', { replace: true });
    } catch (ex) {
      if (isApiError(ex) && ex.code === 'account_locked') {
        const secs = Number(ex.details?.['retryAfterSeconds'] ?? 900);
        setLockedUntil(Date.now() + secs * 1000);
      } else if (isApiError(ex)) {
        const left = ex.details?.['attemptsLeft'];
        setErr({ left: typeof left === 'number' ? left : undefined });
      } else setErr({});
    } finally {
      setBusy(false);
    }
  };

  if (lockedUntil && lockedUntil > now) {
    const left = Math.max(0, Math.round((lockedUntil - now) / 1000));
    return (
      <AuthLayout>
        <div className="hero-ico warn"><Lock size={28} /></div>
        <h1>Too many attempts</h1>
        <p className="sub">To protect your account, sign-in is paused after 5 failed attempts. We’ve emailed the account owner a security notice.</p>
        <div className="lockout">
          <div className="muted">You can try again in</div>
          <div className="timer">{String(Math.floor(left / 60)).padStart(2, '0')}:{String(left % 60).padStart(2, '0')}</div>
          <div className="muted">or reset your password now</div>
        </div>
        <Link className="ui-btn primary btn-block" to="/forgot">Reset password</Link>
        <div className="help">Not you trying to sign in? <Link to="/forgot">Secure your account</Link></div>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout>
      <h1>{t.tMinusDates ? `Welcome back to ${t.home}` : `Sign in to ${s.tenant.appTitle}`}</h1>
      <p className="sub">Sign in to see your {t.items}, {t.crew.toLowerCase()} and deadlines.</p>
      {expired && (
        <div className="alert info"><Info size={18} /><div><b>Your session has expired</b><span>For your security you were signed out. Sign in again to pick up where you left off.</span></div></div>
      )}
      {err && (
        <div className="alert danger" role="alert">
          <CircleAlert size={18} />
          <div>
            <b>{email && pw ? 'Email or password is incorrect' : 'Enter your email and password'}</b>
            {err.left !== undefined && <span>{err.left} attempt{err.left === 1 ? '' : 's'} left before sign-in is paused.</span>}
          </div>
        </div>
      )}
      <form noValidate onSubmit={(e) => void submit(e)}>
        <div className="field">
          <label className="ui-label" htmlFor="si-email">Work email</label>
          <input className="ui-input" id="si-email" type="email" autoComplete="username" inputMode="email" placeholder="you@company.com" value={email} onChange={(e) => setEmail(e.target.value)} required />
        </div>
        <div className="field">
          <div className="field-row"><label className="ui-label" htmlFor="si-pw">Password</label><Link to="/forgot">Forgot password?</Link></div>
          <PwInput id="si-pw" value={pw} onChange={setPw} auto="current-password" placeholder="Enter your password" />
        </div>
        <div className="row-between">
          <label className="check">
            <input type="checkbox" checked={remember} onChange={(e) => setRemember(e.target.checked)} />
            <span className="bx"><Check size={12} strokeWidth={3.5} /></span>
            Keep me signed in for 30 days
          </label>
        </div>
        <Button className="btn-block" variant="primary" type="submit" loading={busy}>Sign in</Button>
      </form>
      <div className="help">Having trouble? Contact your workspace admin.</div>
    </AuthLayout>
  );
}

/* ---------------- Forgot password + "check your inbox" ---------------- */
const mask = (e: string) => {
  const [u = '', d = ''] = e.split('@');
  return `${u.slice(0, 2)}•••@${d}`;
};
export function ForgotPassword() {
  const s = useSession();
  const [email, setEmail] = useState('');
  const [err, setErr] = useState<string | null>(null);
  const [sent, setSent] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [cool, setCool] = useState(0);
  useEffect(() => setPageTitle('Forgot password'), []);
  useEffect(() => {
    if (cool <= 0) return;
    const i = setTimeout(() => setCool((c) => c - 1), 1000);
    return () => clearTimeout(i);
  }, [cool]);
  const send = async (e?: FormEvent) => {
    e?.preventDefault();
    if (!/^\S+@\S+\.\S+$/.test(email)) {
      setErr('Enter the email address you sign in with');
      return;
    }
    setBusy(true);
    setErr(null);
    try {
      const r = await api.call('auth.forgotPassword', { body: { email: email.trim() } });
      setSent(email.trim());
      setCool(r.resendAfterSeconds || 60);
    } catch (ex) {
      setErr(isApiError(ex) && ex.status === 429 ? 'Too many requests. Wait a minute and try again.' : 'Couldn’t send the link. Try again.');
    } finally {
      setBusy(false);
    }
  };
  if (sent)
    return (
      <AuthLayout>
        <div className="env" aria-hidden="true"><div className="back" /><div className="letter"><i /><i /><i /></div><div className="front" /><div className="badge"><Check size={14} strokeWidth={3.5} /></div></div>
        <h1>Check your inbox</h1>
        <p className="sub">If an account exists for <b>{mask(sent)}</b>, you’ll get a reset link shortly. It expires in <b>30 minutes</b> and works once.</p>
        <div className="mail-apps">
          <a className="ui-btn" href="https://mail.google.com" target="_blank" rel="noopener">Open Gmail</a>
          <a className="ui-btn" href="https://outlook.office.com/mail" target="_blank" rel="noopener">Open Outlook</a>
        </div>
        <div className="resend" aria-live="polite">
          <svg className="ring" viewBox="0 0 22 22" aria-hidden="true">
            <circle className="bg" cx="11" cy="11" r="9" />
            <circle className="fg" cx="11" cy="11" r="9" style={{ strokeDashoffset: `${(1 - cool / 60) * 56.5}` }} />
          </svg>
          <span>Didn’t get it?</span>
          <button type="button" className="linkbtn" disabled={cool > 0 || busy} onClick={() => void send()}>{cool > 0 ? `Resend in ${cool}s` : 'Resend link'}</button>
        </div>
        <div className="help">Wrong email? <button type="button" className="linkbtn" onClick={() => setSent(null)}>Try again</button> · Check your spam folder too</div>
      </AuthLayout>
    );
  return (
    <AuthLayout>
      <Link to="/login" className="muted" style={{ display: 'inline-flex', gap: 6, alignItems: 'center', marginBottom: 22, fontWeight: 600 }}><ArrowLeft size={16} />Back to sign in</Link>
      <div className="hero-ico"><KeyRound size={28} /></div>
      <h1>Forgot your password?</h1>
      <p className="sub">Enter the email you use for <b>{s.tenant.appTitle}</b>. We’ll send you a secure link to set a new password.</p>
      <form noValidate onSubmit={(e) => void send(e)}>
        <div className="field">
          <label className="ui-label" htmlFor="fp-email">Work email</label>
          <input className="ui-input" id="fp-email" type="email" autoComplete="email" inputMode="email" placeholder="you@company.com" value={email} onChange={(e) => setEmail(e.target.value)} aria-invalid={!!err} />
          {err && <div className="ui-error" role="alert">{err}</div>}
        </div>
        <Button className="btn-block" variant="primary" type="submit" loading={busy}>Send reset link</Button>
      </form>
      <div className="help">Remembered it? <Link to="/login">Sign in</Link></div>
    </AuthLayout>
  );
}

/* ---------------- Reset password ---------------- */
export function ResetPassword() {
  const { token = '' } = useParams();
  const check = useApi('auth.resetCheck', { params: { token } }, { enabled: token.length >= 20 });
  const [pw, setPw] = useState('');
  const [pw2, setPw2] = useState('');
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  useEffect(() => setPageTitle('Reset password'), []);
  const ok = pwScore(pw) === 4 && pw === pw2;
  if (token.length < 20 || check.data?.state === 'expired' || check.data?.state === 'used' || check.data?.state === 'invalid' || check.isError)
    return (
      <AuthLayout>
        <div className="hero-ico danger"><Link2Off size={28} /></div>
        <h1>This link has expired</h1>
        <p className="sub">Password reset links work <b>once</b> and expire after <b>30 minutes</b>. This one has already been used or is too old. Request a new one below.</p>
        <Link className="ui-btn primary btn-block" to="/forgot">Request a new link</Link>
        <div className="help">Already reset it? <Link to="/login">Sign in</Link></div>
      </AuthLayout>
    );
  if (check.isPending)
    return (
      <AuthLayout>
        <div style={{ display: 'grid', placeItems: 'center', padding: 40 }}><Radar size={56} /></div>
      </AuthLayout>
    );
  if (done)
    return (
      <AuthLayout>
        <div className="hero-ico ok"><Check size={30} strokeWidth={2.5} /></div>
        <h1>Password updated</h1>
        <p className="sub">Your password has been changed and <b>all other sessions were signed out</b>. We’ve sent a confirmation to your email.</p>
        <Link className="ui-btn primary btn-block" to="/login">Continue to sign in</Link>
      </AuthLayout>
    );
  return (
    <AuthLayout>
      <div className="hero-ico"><ShieldCheck size={28} /></div>
      <h1>Set a new password</h1>
      <p className="sub">For <b>{check.data?.maskedEmail}</b>. After you save, you’ll be signed out on all other devices.</p>
      {err && <div className="alert danger" role="alert"><CircleAlert size={18} /><div><b>{err}</b></div></div>}
      <form
        noValidate
        onSubmit={async (e) => {
          e.preventDefault();
          if (!ok) return;
          setBusy(true);
          try {
            await api.call('auth.resetPassword', { body: { token, password: pw } });
            setDone(true);
          } catch (ex) {
            setErr(isApiError(ex) && (ex.code === 'token_expired' || ex.code === 'token_used') ? 'This link has expired. Request a new one.' : 'Couldn’t update your password. Try again.');
          } finally {
            setBusy(false);
          }
        }}
      >
        <div className="field">
          <label className="ui-label" htmlFor="rp-1">New password</label>
          <PwInput id="rp-1" value={pw} onChange={setPw} auto="new-password" placeholder="Create a strong password" describedBy="rp-rules" />
          <PasswordRules pw={pw} id="rp-rules" />
        </div>
        <div className="field">
          <label className="ui-label" htmlFor="rp-2">Confirm new password</label>
          <PwInput id="rp-2" value={pw2} onChange={setPw2} auto="new-password" placeholder="Re-enter password" />
          <div className={`match${pw2 ? (pw === pw2 ? ' ok' : ' bad') : ''}`} aria-live="polite">{pw2 ? (pw === pw2 ? 'Passwords match' : 'Passwords don’t match yet') : ''}</div>
        </div>
        <Button className="btn-block" variant="primary" type="submit" loading={busy} disabled={!ok}>Update password</Button>
      </form>
    </AuthLayout>
  );
}

/* ---------------- Accept invitation ---------------- */
export function AcceptInvite() {
  const { token = '' } = useParams();
  const s = useSession();
  const nav = useNavigate();
  const pre = useApi('auth.invitePreview', { params: { token } }, { enabled: token.length >= 20 });
  const [name, setName] = useState('');
  const [pw, setPw] = useState('');
  const [terms, setTerms] = useState(false);
  const [busy, setBusy] = useState(false);
  const [errs, setErrs] = useState<Record<string, string>>({});
  useEffect(() => setPageTitle('Join workspace'), []);
  const p = pre.data;
  const invalid = useMemo(() => token.length < 20 || pre.isError || (p && p.state !== 'valid'), [token, pre.isError, p]);
  if (invalid)
    return (
      <AuthLayout>
        <div className="hero-ico danger"><Link2Off size={28} /></div>
        <h1>{p?.state === 'accepted' ? 'Invitation already used' : 'This invitation is no longer valid'}</h1>
        <p className="sub">{p?.state === 'accepted' ? 'You’ve already joined. Sign in to continue.' : 'It may have expired or been revoked. Ask your workspace admin to send a new one.'}</p>
        <Link className="ui-btn primary btn-block" to="/login">Go to sign in</Link>
      </AuthLayout>
    );
  if (!p)
    return (
      <AuthLayout>
        <div style={{ display: 'grid', placeItems: 'center', padding: 40 }}><Radar size={56} /></div>
      </AuthLayout>
    );
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    const er: Record<string, string> = {};
    if (name.trim().length < 2) er['name'] = 'Please enter your name';
    if (pwScore(pw) < 4) er['pw'] = 'Use a stronger password (see the rules)';
    if (!terms) er['terms'] = 'Please accept the terms to continue';
    setErrs(er);
    if (Object.keys(er).length) return;
    setBusy(true);
    try {
      const r = await api.call('auth.acceptInvite', { body: { token, name: name.trim(), password: pw, acceptTerms: true } });
      s.adopt(r.accessToken, r.me);
      nav('/', { replace: true });
    } catch (ex) {
      setErrs({ form: isApiError(ex) ? ex.message : 'Couldn’t create your account. Try again.' });
    } finally {
      setBusy(false);
    }
  };
  return (
    <AuthLayout>
      <div className="inviter">
        <div className="av">{(p.inviterName ?? '?').split(' ').map((x) => x[0]).join('').slice(0, 2)}</div>
        <div className="t">
          <b>{p.inviterName}</b> invited you to join <b>{p.tenantName}</b>
          <div className="chips">
            {p.workspaces.map((w) => <span className="chip" key={w}><i />Workspace: {w}</span>)}
            {p.roleName && <span className="chip">Role: {p.roleName}</span>}
          </div>
        </div>
      </div>
      <h1>Create your account</h1>
      <p className="sub">Set up your profile to start working with the team.</p>
      {errs['form'] && <div className="alert danger" role="alert"><CircleAlert size={18} /><div><b>{errs['form']}</b></div></div>}
      <form noValidate onSubmit={(e) => void submit(e)}>
        <div className="field">
          <label className="ui-label" htmlFor="in-email">Email</label>
          <div className="pw-wrap"><input className="ui-input" id="in-email" type="email" value={p.email ?? ''} readOnly autoComplete="username" /><Lock className="lock-ico" size={16} /></div>
        </div>
        <div className="field">
          <label className="ui-label" htmlFor="in-name">Full name</label>
          <input className="ui-input" id="in-name" type="text" autoComplete="name" placeholder="e.g. Rohan Joshi" value={name} onChange={(e) => setName(e.target.value)} aria-invalid={!!errs['name']} />
          {errs['name'] && <div className="ui-error" role="alert">{errs['name']}</div>}
        </div>
        <div className="field">
          <label className="ui-label" htmlFor="in-pw">Password</label>
          <PwInput id="in-pw" value={pw} onChange={setPw} auto="new-password" placeholder="At least 10 characters" describedBy="in-rules" />
          <PasswordRules pw={pw} id="in-rules" />
          {errs['pw'] && <div className="ui-error" role="alert">{errs['pw']}</div>}
        </div>
        <div className="field">
          <label className="check">
            <input type="checkbox" checked={terms} onChange={(e) => setTerms(e.target.checked)} />
            <span className="bx"><Check size={12} strokeWidth={3.5} /></span>
            <span>I agree to the <a href="https://devninjaweb.com/terms" target="_blank" rel="noopener">Terms</a> and <a href="https://devninjaweb.com/privacy" target="_blank" rel="noopener">Privacy Policy</a></span>
          </label>
          {errs['terms'] && <div className="ui-error" role="alert">{errs['terms']}</div>}
        </div>
        <Button className="btn-block" variant="primary" type="submit" loading={busy}>Join workspace</Button>
      </form>
    </AuthLayout>
  );
}
